import { OverlayModule, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { PortalModule, TemplatePortal } from '@angular/cdk/portal';
import { NgClass, NgTemplateOutlet, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  TemplateRef,
  ViewContainerRef,
  inject,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslocoCoreModule } from '@core/transloco/transloco.module';
import { ProjectType } from '@shared/enums/project.enum';
import { NotificationsService } from '@shared/service/notifications.service';
import { ProjectService } from '@shared/service/project.service';
import { CurrentUserQuery } from '@state/auth-state/current-user.query';
import { CurrentUserState } from '@state/auth-state/current-user.store';
import { CurrentProjectQuery } from '@state/project-state/current-project.query';
import {
  CurrentProjectState,
  CurrentProjectStore
} from '@state/project-state/current-project.store';
import { forkJoin } from 'rxjs';
import { filter } from 'rxjs/operators';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'notifications',
  templateUrl: './notifications.component.html',
  styles: [
    `
      ::ng-deep .mat-tooltip {
        font-size: 14px;
      }
    `
  ],
  exportAs: 'notifications',
  imports: [
    NgClass,
    NgTemplateOutlet,
    DatePipe,
    OverlayModule,
    PortalModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslocoCoreModule
  ]
})
export class NotificationsComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private _notificationsService = inject(NotificationsService);
  private _overlay = inject(Overlay);
  private _viewContainerRef = inject(ViewContainerRef);
  private readonly currentUserQuery = inject(CurrentUserQuery);
  private readonly router = inject(Router);
  private readonly currentProjectStore = inject(CurrentProjectStore);
  private readonly currentProjectQuery = inject(CurrentProjectQuery);
  private readonly projectService = inject(ProjectService);
  private readonly _notificationsOrigin = viewChild<MatButton>('notificationsOrigin');
  private readonly _notificationsPanel = viewChild<TemplateRef<any>>('notificationsPanel');
  currentAccessType: string;
  currentOrganizationId: string;
  currentUser: any;
  currentProjectData: any;
  notifications: any[];
  notificationGroups: any[] = [];
  unreadCount: number = 0;
  private _overlayRef: OverlayRef;
  isPanelClose: boolean = false;
  isLoading: boolean;
  private hasLoadedNotificationsAfterLogin = false;
  constructor() {
    this._notificationsService.overlaySubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((_res: any) => {
        this.isPanelClose = true;
        this.closePanel();
        this.cdr.markForCheck();
      });
  }
  ngOnInit(): void {
    this.getCurrentUserDataFromState();
    this.getCurrentProjectDataFromState();
    this.refreshNotificationsOnProjectAuditNavigation();
  }
  openPanel(): void {
    // Return if the notifications panel or its origin is not defined
    const _notificationsPanel = this._notificationsPanel();
    if (!_notificationsPanel || !this._notificationsOrigin()) {
      return;
    }
    // Create the overlay if it doesn't exist
    if (!this._overlayRef) {
      this._createOverlay();
    }
    // Attach the portal to the overlay
    this._overlayRef.attach(new TemplatePortal(_notificationsPanel, this._viewContainerRef));
    this.getNotifications();
  }
  closePanel(): void {
    if (this._overlayRef) this._overlayRef.detach();
  }
  markAllAsRead(): void {
    const unreadNotifications = (this.notifications || []).filter(
      notification => !notification.seen
    );
    if (!unreadNotifications.length) {
      return;
    }
    unreadNotifications.forEach(notification => this.setNotificationSeenState(notification, true));
    this._notificationsService
      .updateActivityNotifications({
        activityLogIds: unreadNotifications.map(notification => notification.id),
        seen: true,
        archived: false
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => {
          this.getNotifications();
          this.cdr.markForCheck();
        }
      });
  }
  toggleRead(notification: any, _index: number): void {
    if (!notification?.id) {
      return;
    }
    const nextSeen = !notification.seen;
    this.setNotificationSeenState(notification, nextSeen);
    this._notificationsService
      .updateActivityNotification(notification.id, {
        seen: nextSeen,
        archived: false
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => {
          this.setNotificationSeenState(notification, !nextSeen);
          this.cdr.markForCheck();
        }
      });
  }
  delete(notification: any, _index: number): void {
    if (!this.canManageNotification(notification)) {
      return;
    }
    this._notificationsService.delete(notification.id).subscribe((_res: any) => {
      this.getNotifications();
      this.cdr.markForCheck();
    });
  }
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }
  trackByGroup(index: number, item: any): any {
    return item.date || index;
  }
  isFootprintActivityNotification(notification: any): boolean {
    return (
      notification?.activityType === 'FOOTPRINT' ||
      notification?.activityEvent?.startsWith?.('FOOTPRINT') ||
      !!notification?.footprintId ||
      !!notification?.requestPayload?.footprintId
    );
  }
  openNotification(notification: any): void {
    if (!this.isFootprintActivityNotification(notification)) {
      return;
    }
    const context = notification?.metadata?.context || {};
    const footprintId = notification?.footprintId || notification?.requestPayload?.footprintId;
    const project = notification?.projects?.[0];
    const factoryId =
      notification?.footprint?.factoryId ||
      notification?.metadata?.organizationId ||
      notification?.organization?.id;
    const factoryName =
      notification?.footprint?.factory?.name ||
      context.factoryName ||
      notification?.metadata?.organizationName ||
      notification?.organization?.name;
    const targetPath = this.getNotificationTargetPath(project?.projectType);
    this.markActivityNotificationAsSeen(notification);
    this.closePanel();
    if (project?.id && !this.isFactoryUser()) {
      this.projectService
        .getV3ById(project.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res: any) => {
            this.navigateToActivityNotification(
              notification,
              res?.data || project,
              factoryId,
              factoryName,
              targetPath,
              footprintId,
              context
            );
            this.cdr.markForCheck();
          },
          error: () => {
            this.navigateToActivityNotification(
              notification,
              project,
              factoryId,
              factoryName,
              targetPath,
              footprintId,
              context
            );
            this.cdr.markForCheck();
          }
        });
      return;
    }
    this.navigateToActivityNotification(
      notification,
      project,
      factoryId,
      factoryName,
      targetPath,
      footprintId,
      context
    );
  }
  private navigateToActivityNotification(
    notification: any,
    project: any,
    factoryId: string,
    factoryName: string,
    targetPath: string,
    footprintId: string,
    context: any
  ): void {
    this.prepareProjectContext(notification, project, factoryId, factoryName, targetPath);
    this._notificationsService.setActivityNavigationTarget({
      footprintId,
      year: context.year,
      month: context.month,
      moduleName: context.moduleName,
      footprintType: context.footprintType,
      notificationClick: `${notification?.id || footprintId || 'notification'}-${Date.now()}`
    });
    const queryParams: any = {};
    if (this.isFactoryUser()) {
      queryParams.page = 'verification';
    }
    this.router.navigate([targetPath], {
      queryParams,
      queryParamsHandling: ''
    });
  }
  canManageLegacyNotifications(): boolean {
    return true;
  }
  canManageNotification(notification: any): boolean {
    return !!notification?.id;
  }
  canRemoveNotification(notification: any): boolean {
    return !notification?.isActivityNotification;
  }
  private prepareProjectContext(
    notification: any,
    project: any,
    factoryId: string,
    factoryName: string,
    targetPath: string
  ): void {
    if (!project?.id || !factoryId) {
      return;
    }
    const context = notification?.metadata?.context || {};
    const year = context.year || new Date(notification?.createdAt || new Date()).getFullYear();
    const projectInfo = this.normalizeProjectInfoForState(project, year);
    const projectBasePath = targetPath.startsWith('/audit')
      ? '/audit'
      : targetPath.startsWith('/online-audit')
        ? '/online-audit'
        : '/project';
    const factoryPath = this.isFactoryUser() ? targetPath : `${projectBasePath}/factory`;
    this.currentProjectStore.set({
      projectId: project.id,
      factoryId,
      factoryName,
      projectInfo
    });
    localStorage.setItem(
      'breadcrumbs',
      JSON.stringify([
        {
          name: `Project : ${projectInfo.name}`,
          path: projectBasePath,
          query: { projectId: project.id }
        },
        {
          name: `Factory : ${factoryName || 'Factory'}`,
          path: factoryPath,
          query: {
            projectId: project.id,
            factoryId
          }
        },
        {
          name: 'Verification',
          path: targetPath,
          query: {
            projectId: project.id,
            factoryId
          }
        }
      ])
    );
  }
  private normalizeProjectInfoForState(project: any, year: number): any {
    const durationStartDate =
      project?.durationStartDate || project?.durationStart || `${year}-01-01`;
    const durationEndDate = project?.durationEndDate || project?.durationEnd || `${year}-12-31`;
    return {
      ...project,
      id: project.id,
      name: project.name,
      durationStartDate,
      durationEndDate,
      durationStart: durationStartDate,
      durationEnd: durationEndDate,
      totalFollowUp: Number(project?.totalFollowUp ?? project?.numberOfFollowUp),
      projectManagementModule: project.projectManagementModule || {
        hasChemicalManagement: false
      },
      packageModules: project.packageModules || [{}, {}]
    };
  }
  private getNotificationTargetPath(projectType: string): string {
    if (this.isFactoryUser()) {
      return projectType === 'ONLINE_AUDIT' ? '/online-audit/details' : '/project/details';
    }
    return projectType === 'ONLINE_AUDIT'
      ? '/audit/factory/details/verification'
      : '/project/factory/details/verification';
  }
  private markActivityNotificationAsSeen(notification: any): void {
    if (!notification?.id || notification.seen) {
      return;
    }
    this.setNotificationSeenState(notification, true);
    this._notificationsService
      .updateActivityNotification(notification.id, {
        seen: true,
        archived: false
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => {
          this.setNotificationSeenState(notification, false);
          this.cdr.markForCheck();
        }
      });
  }
  private _createOverlay(): void {
    // Create the overlay
    this._overlayRef = this._overlay.create({
      hasBackdrop: true,
      backdropClass: 'fuse-backdrop-on-mobile',
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._notificationsOrigin()._elementRef.nativeElement)
        .withLockedPosition(true)
        .withPush(true)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top'
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom'
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top'
          },
          {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom'
          }
        ])
    });
    // Detach the overlay from the portal on backdrop click
    this._overlayRef.backdropClick().subscribe(() => {
      this._overlayRef.detach();
    });
  }
  private getCurrentUserDataFromState(): void {
    this.currentUserQuery
      .select()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state: CurrentUserState) => {
        const currentUser: any = state.data;
        this.currentUser = currentUser;
        this.currentOrganizationId = currentUser?.organization?.id || currentUser?.organizationId;
        this.currentAccessType = currentUser?.userType;
        if (
          this.canLoadActivityNotifications() &&
          currentUser?.id &&
          !this.hasLoadedNotificationsAfterLogin
        ) {
          this.hasLoadedNotificationsAfterLogin = true;
          this.getNotifications();
        }
        this.cdr.markForCheck();
      });
  }
  private refreshNotificationsOnProjectAuditNavigation(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationEnd) => {
        const isProjectOrAuditRoute = this.isProjectOrAuditRootRoute(
          event.urlAfterRedirects || event.url
        );
        if (!isProjectOrAuditRoute) {
          return;
        }
        if (this.canLoadActivityNotifications()) {
          this.getNotifications();
        }
        this.cdr.markForCheck();
      });
  }
  private canLoadActivityNotifications(): boolean {
    return this.isConsultantUser() || this.isFactoryUser();
  }
  private isConsultantUser(): boolean {
    return (
      !this.isFactoryUser() &&
      (this.currentUser?.userType === 'CONSULTANT' ||
        this.currentUser?.permissions?.profile === 'CONSULTANT' ||
        this.currentUser?.viewAs === 'from_admin_to_consultant')
    );
  }
  private isFactoryUser(): boolean {
    return (
      this.currentUser?.userType === 'FACTORY' ||
      this.currentUser?.permissions?.profile === 'FACTORY' ||
      this.currentUser?.viewAs === 'from_admin_to_factory' ||
      !!this.currentUser?.organization?.factorySubscriptionPlan
    );
  }
  private isProjectOrAuditRootRoute(url: string): boolean {
    const path = url.split('?')[0].split('#')[0].replace(/\/$/, '');
    return (
      path === '/project' ||
      path === '/audit' ||
      path === '/online-audit' ||
      path.endsWith('/project') ||
      path.endsWith('/audit') ||
      path.endsWith('/online-audit')
    );
  }
  getNotifications() {
    this.isLoading = true;
    if (this.isFactoryUser()) {
      this.getFactoryNotifications();
      return;
    }
    if (!this.isConsultantUser()) {
      this.resetNotifications();
      return;
    }
    this.getConsultantNotifications();
  }
  private getConsultantNotifications(): void {
    const projectIds = this.getConsultantProjectIds();
    if (projectIds.length) {
      this.fetchActivityNotifications(
        projectIds,
        'CONSULTANT',
        undefined,
        this.currentOrganizationId,
        100
      );
      return;
    }
    this.getConsultantProjectIdsFromApi();
  }
  private getFactoryNotifications(): void {
    const factoryId = this.currentOrganizationId;
    if (!factoryId) {
      this.resetNotifications();
      return;
    }
    this.getFactoryProjectIdsFromApi(factoryId);
  }
  private fetchActivityNotifications(
    projectIds: string[],
    notificationViewer: 'CONSULTANT' | 'FACTORY',
    factoryId?: string,
    consultantId?: string,
    take: number = 50
  ): void {
    if (!projectIds.length) {
      this.resetNotifications();
      return;
    }
    this._notificationsService
      .getActivityNotifications({
        take,
        page: 1,
        notificationViewer,
        projectIds,
        factoryId,
        consultantId: notificationViewer === 'CONSULTANT' ? consultantId : undefined
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const groups = Array.isArray(res?.data) ? res.data : [];
          this.notificationGroups = this.mapNotificationGroups(groups);
          this.notifications = this.notificationGroups.flatMap(
            (group: any) => group.notifications || []
          );
          this.syncUnreadCount();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.resetNotifications();
          this.cdr.markForCheck();
        }
      });
  }
  private getConsultantProjectIdsFromApi(): void {
    const consultantId = this.currentOrganizationId;
    if (!consultantId) {
      this.resetNotifications();
      return;
    }
    forkJoin([
      this.projectService.getAllV3WithFilters({
        consultantId,
        projectType: ProjectType.PROJECT,
        page: 1,
        take: 100
      }),
      this.projectService.getAllV3WithFilters({
        consultantId,
        projectType: ProjectType.ONLINE_AUDIT,
        page: 1,
        take: 100
      })
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([projectRes, auditRes]: any[]) => {
          const projectIds = this.getProjectIdsFromProjectResponses([projectRes, auditRes]);
          this.fetchActivityNotifications(projectIds, 'CONSULTANT', undefined, consultantId, 100);
          this.cdr.markForCheck();
        },
        error: () => {
          this.resetNotifications();
          this.cdr.markForCheck();
        }
      });
  }
  private getConsultantProjectIds(): string[] {
    const projectIds = new Set<string>();
    if (this.currentProjectData?.projectId) {
      projectIds.add(this.currentProjectData.projectId);
    }
    const projectFactories = this.currentUser?.permissions?.scope?.PROJECT_FACTORIES;
    if (Array.isArray(projectFactories)) {
      projectFactories.forEach((item: any) => {
        if (item?.projectId) {
          projectIds.add(item.projectId);
        }
      });
    }
    return Array.from(projectIds);
  }
  private getFactoryProjectIdsFromApi(factoryId: string): void {
    this.projectService
      .getAllV3WithFilters({
        factoryId,
        page: 1,
        take: 100
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (projectRes: any) => {
          const projectIds = this.getProjectIdsFromProjectResponses([projectRes]);
          this.fetchActivityNotifications(projectIds, 'FACTORY', factoryId, undefined, 100);
          this.cdr.markForCheck();
        },
        error: () => {
          this.resetNotifications();
          this.cdr.markForCheck();
        }
      });
  }
  private getCurrentProjectDataFromState(): void {
    this.currentProjectQuery
      .select()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state: CurrentProjectState) => {
        this.currentProjectData = state.data;
        this.cdr.markForCheck();
      });
  }
  private getProjectIdsFromProjectResponses(responses: any[]): string[] {
    const projectIds = new Set<string>();
    responses.forEach(response => {
      const projects = Array.isArray(response?.data) ? response.data : [];
      projects.forEach((project: any) => {
        if (project?.id) {
          projectIds.add(project.id);
        }
      });
    });
    return Array.from(projectIds);
  }
  private resetNotifications(): void {
    this.notifications = [];
    this.notificationGroups = [];
    this.unreadCount = 0;
    this.isLoading = false;
  }
  private mapActivityNotification(notification: any): any {
    const status = notification?.notification;
    const displayOrganizationName = this.isFactoryUser()
      ? notification?.organization?.name ||
        notification?.metadata?.organizationName ||
        notification?.metadata?.context?.factoryName
      : notification?.metadata?.context?.factoryName ||
        notification?.footprint?.factory?.name ||
        notification?.organization?.name ||
        notification?.metadata?.organizationName;
    return {
      ...notification,
      title: notification?.description?.title || notification?.title,
      text: notification?.description?.details || notification?.text,
      factoryName: displayOrganizationName,
      seen: status ? status.seen === true : false,
      archived: status?.archived === true,
      icon: notification?.icon || 'heroicons_outline:bell',
      isActivityNotification: true
    };
  }
  private mapNotificationGroups(groups: any[]): any[] {
    return groups.map(group => {
      const notifications = (group?.notifications || []).map((notification: any) =>
        this.mapActivityNotification(notification)
      );
      return {
        ...group,
        notifications,
        unreadCount: notifications.filter((notification: any) => !notification.seen).length,
        totalCount: notifications.length
      };
    });
  }
  private setNotificationSeenState(notification: any, seen: boolean): void {
    notification.seen = seen;
    if (!notification.notification) {
      notification.notification = {};
    }
    notification.notification.seen = seen;
    notification.notification.archived = false;
    this.syncNotificationGroupCounts();
    this.syncUnreadCount();
  }
  private syncNotificationGroupCounts(): void {
    this.notificationGroups.forEach(group => {
      const notifications = group.notifications || [];
      group.totalCount = notifications.length;
      group.unreadCount = notifications.filter((notification: any) => !notification.seen).length;
    });
  }
  private syncUnreadCount(): void {
    this.unreadCount = (this.notifications || []).filter(notification => !notification.seen).length;
  }
}
